import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Flex, Form, Switch, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { CredentialsForm } from './CredentialsForm';

const { Text } = Typography;

export const ConfigCard = forwardRef(({ index, fullSchema, existingConfigurations, defaultSedeId, readOnly, isNewSede, onRemove, showSwitch = true, externalEnabled, onEnabledChange, onSedeSelect }, ref) => {
  const [form] = Form.useForm();
  const [selectedSede, setSelectedSede] = useState(defaultSedeId ?? '__general__');
  const [enabled, setEnabled] = useState(true);

  const isControlled = onEnabledChange !== undefined;
  const switchChecked = isControlled ? externalEnabled : enabled;
  const switchOnChange = isControlled ? onEnabledChange : setEnabled;

  // '__general__' es el centinela para sede=null (General)
  const isGeneral = selectedSede === '__general__';
  const currentConfig = isNewSede
    ? null
    : isGeneral
      ? (existingConfigurations?.find((c) => c.sede === null) ?? null)
      : (existingConfigurations?.find((c) => c.sede?.id === selectedSede) ?? null);

  const loadConfig = useCallback((config) => {
    form.resetFields();
    if (config) {
      form.setFieldsValue({ sede: config.sede?.id ?? '__general__', ...config.credentials });
      setEnabled(config.enabled);
    } else if (!isNewSede) {
      form.setFieldsValue({ sede: selectedSede });
      setEnabled(true);
    }
  }, [form, selectedSede, isNewSede]);

  useEffect(() => {
    setSelectedSede(defaultSedeId ?? '__general__');
  }, [defaultSedeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNewSede) {
      loadConfig(currentConfig);
    }
  }, [currentConfig?.id ?? `null-${selectedSede}`, selectedSede]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    isTouched: () => form.isFieldsTouched(),
    getValues: async () => {
      const fields = await form.validateFields();

      if (isNewSede) {
        const { sedeName, ...credentials } = fields;
        return {
          sedeName,
          credentials,
          enabled,
          existingId: null,
        };
      }

      const { sede, ...credentials } = fields;
      return {
        branchId: sede === '__general__' ? null : sede,
        credentials,
        enabled: isControlled ? externalEnabled : enabled,
        existingId: currentConfig?.id ?? null,
      };
    },
  }));

  const handleSedeChange = (value) => {
    setSelectedSede(value);
    onSedeSelect?.(value);
  };

  return (
    <div style={{
      border: '1px solid #e0e0e0',
      borderRadius: 12,
      padding: '20px 20px 8px',
      backgroundColor: '#ffffff',
    }}>
      <Flex align='center' gap={12} style={{ marginBottom: 14 }}>
        {showSwitch && (
          <>
            <Switch
              size='small'
              checked={switchChecked}
              onChange={switchOnChange}
              disabled={readOnly}
            />
            <Text strong style={{ fontSize: 13, color: '#262626' }}>Habilitar</Text>
          </>
        )}
        {onRemove && (
          <Button
            type='text'
            size='small'
            danger
            icon={<FontAwesomeIcon icon={faXmark} />}
            onClick={onRemove}
            style={{ marginLeft: 'auto', padding: '0 8px' }}
          />
        )}
      </Flex>

      {fullSchema && (
        <CredentialsForm
          form={form}
          initialCredentials={isNewSede ? {} : { sede: defaultSedeId ?? '__general__' }}
          credentialsSchema={fullSchema}
          readOnly={readOnly}
          onSedeChange={isNewSede ? undefined : handleSedeChange}
        />
      )}
    </div>
  );
});
